import { BlobResponse, IBooleanResponse, SecuredRestService } from "shared";
import { AppLang } from "app";
import {
    formExpired,
    IBusinessGroup,
    IExternalUrlResponse,
    IFileSource,
    IHistoryRecord,
    IHistoryResponse,
    IOrganization,
    IAutonumberCounter,
    IAutonumberCounterFilters,
    IAutonumberCounterSaveRequest,
    IssueType,
    ITemplate,
    ITemplateComponent,
    ITemplateListResponse,
    ITemplatesService,
    ITicketTypesResponse,
    IUploadFileResponse,
    TicketField
} from "../types/template_service_types";
import { addTemplateComponentId } from "../utils/template-components";

const localRecentlyEditedTemplates = "recentlyEditedTemplates";

export type CommentFormat = "FULL" | "SHORT";

export interface AmendmentsAnalysisSettings {
    analysisSide: "CUSTOMER" | "EXECUTOR";
    nonMatrixAnalysisEnabled: boolean;
    nonMatrixPlaceholder: string;
    commentFormat: CommentFormat;
}

export class TemplatesService extends SecuredRestService implements ITemplatesService {
    loadTemplates = (extUserId: string | null = null): Promise<ITemplateListResponse> => {
        const request = extUserId ? {
            extUserId
        } : undefined;
        return this.get<ITemplate[]>({
            url: "template/names",
            request: request
        }).then(res => {
            if (Array.isArray(res)) {
                return {
                    expired: false,
                    templateList: res
                };
            } else {
                if (res === formExpired) {
                    return {
                        expired: true,
                        templateList: []
                    };
                }
                try {
                    return {
                        expired: false,
                        templateList: JSON.parse(res) as ITemplate[]
                    };
                } catch (e) {
                    return {
                        expired: false,
                        templateList: []
                    };
                }
            }
        });
    };

    loadTemplate = (id: number, extUserId: string | null = null): Promise<ITemplateComponent[]> => {
        return this.post<string>({
            url: "template/load",
            request: { id, extUserId }
        }).then(res => {
            try {
                return addTemplateComponentId(JSON.parse(res) as Partial<ITemplateComponent>[]);
            } catch (e) {
                return [];
            }
        });
    };

    loadPassword = (id: number, extUserId: string | null = null): Promise<string | undefined> => {
        return this.post<string>({
            url: "template/load_pass",
            request: { id, extUserId }
        }).then(res => {
            return res ? res : undefined;
        });
    };

    savePassword = (id: number, pdfAdminPassword: string): Promise<IBooleanResponse> => {
        return this.post<IBooleanResponse>({
            url: "template/save_pass",
            request: { id, pdfAdminPassword }
        }).then(res => {
            return res;
        });
    };

    getTemplate = (id: number): Promise<IHistoryRecord> => {
        return this.get<IHistoryRecord[]>({
            url: "template",
            request: { id: id.toString() }
        }).then(res => {
            return res[0];
        });
    };

    saveTemplate = (id: number, text: string) => {
        return this.post<string>({
            url: "template/save",
            request: { id, text }
        }).then(res => {
            return res;
        });
    };

    loadAutonumberCounters = (filters?: IAutonumberCounterFilters): Promise<IAutonumberCounter[]> => {
        const request = filters ? {
            ...(filters.id ? { id: filters.id } : {}),
            ...(filters.name ? { name: filters.name } : {})
        } : undefined;
        return this.get<IAutonumberCounter[]>({
            url: "autonumber-component/list",
            request
        });
    };

    saveAutonumberCounter = (request: IAutonumberCounterSaveRequest): Promise<number> => {
        return this.post<number>({
            url: "autonumber-component",
            request: { ...request },
            dataFormat: "json"
        });
    };

    addTemplate = (group_id: number, code: string, name: string, description: string, userName: string | undefined, userNameEn: string | undefined) => {
        return this.post<number>({
            url: "template/add",
            request: { group_id, code, name, userName, userNameEn, description }
        }).then(res => {
            return res;
        });
    };

    editTemplate = (id: number, group_id: number, code: string, name: string, description: string) => {
        return this.post<string>({
            url: "template/edit",
            request: { id, group_id, code, name, description }
        }).then(res => {
            return res;
        });
    };

    copyTemplate = (id: number, group_id: number, code: string, name: string, description: string, userName: string | undefined, userNameEn: string | undefined) => {
        return this.post<string>({
            url: "template/copy",
            request: { id, group_id, code, name, userName, userNameEn, description }
        }).then(res => {
            return res;
        });
    };

    deleteTemplate = (id: number, group_id: number) => {
        return this.post<string>({
            url: "template/delete",
            request: { id, group_id }
        }).then(res => {
            return res;
        });
    };

    downloadTemplate = (docId: number, xxcdoc_lang: string) => {
        return this.post<BlobResponse>({
            url: "doc/template",
            request: { docId, xxcdoc_lang }
        }).then(res => {
            return res;
        });
    };

    uploadTemplate = (docId: number, fileName: string, file: File) => {
        return this.post<IUploadFileResponse>({
            url: "doc/upload",
            request: { docId, fileName, file }
        }).then(res => {
            return res;
        });
    };

    hasAmendments = (templateId: number): Promise<boolean> => {
        return this.get<{ hasAmendments: boolean }>({
            url: "doc/amendments/has",
            request: { templateId: templateId.toString() }
        }).then(res => res.hasAmendments);
    };

    getAmendmentsStatus = (templateId: number): Promise<{ status: "NONE" | "PROCESSING" | "READY"; uploadedAt: string | null }> => {
        return this.get({
            url: "doc/amendments/status",
            request: { templateId: templateId.toString() }
        });
    };

    uploadAmendments = (templateId: number, file: File, transform?: boolean): Promise<{ status: string }> => {
        return this.post<{ status: string }>({
            url: "doc/amendments",
            request: { templateId, file, ...(transform === undefined ? {} : { transform }) }
        });
    };

    deleteAmendments = (templateId: number): Promise<string> => {
        return this.delete<string>({
            url: "doc/amendments",
            request: { templateId: templateId.toString() }
        });
    };

    downloadAmendments = (templateId: number): Promise<BlobResponse> => {
        return this.get<BlobResponse>({
            url: "doc/amendments/download",
            request: { templateId: templateId.toString() }
        });
    };

    downloadAmendmentsSample = (): Promise<BlobResponse> => {
        return this.get<BlobResponse>({
            url: "doc/amendments/sample"
        });
    };

    getAmendmentsParty = (templateId: number): Promise<{ analysisSide: "CUSTOMER" | "EXECUTOR" }> => {
        return this.get<{ analysisSide: "CUSTOMER" | "EXECUTOR" }>({
            url: "doc/amendments/analysis-side",
            request: { templateId: templateId.toString() }
        });
    };

    getAmendmentsAnalysisSettings = (templateId: number): Promise<AmendmentsAnalysisSettings> => {
        return this.get<AmendmentsAnalysisSettings>({
            url: "doc/amendments/analysis-settings",
            request: { templateId: templateId.toString() }
        });
    };

    setAmendmentsAnalysisSettings = (templateId: number, settings: AmendmentsAnalysisSettings): Promise<{ status: string }> => {
        return this.post<{ status: string }>({
            url: `doc/amendments/analysis-settings?templateId=${templateId}`,
            dataFormat: "json",
            request: { ...settings }
        });
    };

    setAmendmentsParty = (templateId: number, analysisSide: "CUSTOMER" | "EXECUTOR"): Promise<{ status: string }> => {
        return this.post<{ status: string }>({
            url: `doc/amendments/analysis-side?templateId=${templateId}&analysisSide=${analysisSide}`
        });
    };

    getExternalUrl = (templateId: number, state: Record<string, any>): Promise<IExternalUrlResponse> => {
        return this.post<string>({
            url: `ext/url/${templateId}`,
            request: { state: JSON.stringify(state) }
        }).then(res => {
            try {
                return JSON.parse(res) as IExternalUrlResponse;
            } catch (e) {
                return {};
            }
        });
    };

    getPredefinedExternalData = (id: number, extUserId: string | null = null): Promise<Record<string, any>> => {
        return this.post<string>({
            url: `ext/predefined`,
            request: { id, extUserId }
        }).then(res => {
            try {
                return JSON.parse(res) as Record<string, any>;
            } catch (e) {
                return {};
            }
        });
    };

    getExtDefinedData = (id: number, extUserId: string | null = null): Promise<Record<string, any>> => {
        return this.post<string>({
            url: `ext/extdefined`,
            request: { id, extUserId }
        }).then(res => {
            try {
                return JSON.parse(res) as Record<string, any>;
            } catch (e) {
                return {};
            }
        });
    };

    getExtProtectedData = (id: number, extUserId: string | null = null): Promise<Record<string, any>> => {
        return this.post<Record<string, any>[]>({
            url: `ext/extprotected`,
            request: { id, extUserId }
        }).then(res => {
            try {
                if (res && Array.isArray(res)) {
                    return res.reduce((acc, item) => {
                        acc[item.key] = item.value;
                        return acc;
                    }, {});
                } else {
                    return {};
                }
            } catch (e) {
                return {};
            }
        });
    };

    saveExternalForm = (id: number, extUserId: string | null = null, state: Record<string, any>): Promise<string> => {
        return this.post<string>({
            url: `ext/ynd-url/${id}`,
            request: { extUserId, state: JSON.stringify(state) }
        }).then(res => {
            return res;
        });
    };

    postComment = (hist_id: number, template_id: number, hist_end_date: string, comment: string, tzOffset = -180): Promise<IBooleanResponse> => {
        return this.post<IBooleanResponse>({
            url: "history/comment",
            request: { hist_id, template_id, hist_end_date, comment, tzOffset }
        }).then(res => {
            return res;
        });
    };

    loadHistory = (id: number, dateTo: string, dateFrom: string | null, limit?: number, tzOffset = -180) => {
        const request: Record<string, string> = {
            id: id.toString(),
            dateTo,
            tzOffset: tzOffset.toString()
        };

        if (dateFrom) {
            request.dateFrom = dateFrom;
        }

        if (limit) {
            request.limit = limit.toString();
        }

        return this.get<IHistoryResponse>({
            url: "history",
            request: request
        }).then(res => {
            return res;
        });
    };

    downloadHistory = (template_id: number, version_id: number, source: IFileSource, date_time: string, xxcdoc_lang: string, format = "rtf", tzOffset = -180) => {
        return this.post<BlobResponse>({
            url: "history/download",
            request: { template_id, version_id, source, date_time, format, tzOffset, xxcdoc_lang }
        }).then(res => {
            return res;
        });
    };

    loadBusinessGroups = (extUserId: string | null = null): Promise<IBusinessGroup[]> => {
        const request = extUserId ? {
            extUserId
        } : undefined;
        return this.get<IBusinessGroup[]>({
            url: "bgs",
            request: request
        }).then(res => {
            if (Array.isArray(res)) {
                return res;
            } else {
                try {
                    return JSON.parse(res) as IBusinessGroup[];
                } catch (e) {
                    return [];
                }
            }
        });
    };

    loadOrganizations = (extUserId: string | null = null): Promise<IOrganization[]> => {
        const request = extUserId ? {
            extUserId
        } : undefined;
        return this.get<IOrganization[]>({
            url: "orgs",
            request: request
        }).then(res => {
            if (Array.isArray(res)) {
                return res;
            } else {
                try {
                    return JSON.parse(res) as IOrganization[];
                } catch (e) {
                    return [];
                }
            }
        });
    };

    loadTicketTypes = (queue: string, xxcdoc_lang: AppLang): Promise<IssueType[]> => {
        return this.get<ITicketTypesResponse>({
            url: "ticket/types",
            request: { queue, xxcdoc_lang }
        }).then(res => {
            return res?.issueTypes ? res.issueTypes : [];
        });
    };

    loadTicketFields = (xxcdoc_lang: AppLang, queue: string, options?: RequestInit): Promise<TicketField[]> => {
        return this.get<TicketField[]>({
            url: "ticket/fields",
            request: { queue, xxcdoc_lang },
            options
        }).then(res => {
            return res;
        });
    };

    getOrganizationDetails = (ya_name: string, extUserId: string | null = null): Promise<IOrganization> => {
        return this.post<IOrganization[]>({
            url: `orgs/org/${extUserId}`,
            request: { ya_name },
            dataFormat: "json"
        }).then(res => {
            return res[0];
        });
    };

    setRecentlyEditedTemplate = (templateId: number): Promise<number[]> => {
        let recentlyEditedArr: number[] = [];
        try {
            const recentlyEdited = localStorage.getItem(localRecentlyEditedTemplates);
            if (recentlyEdited) {
                recentlyEditedArr = JSON.parse(recentlyEdited);
            }
        } catch (e) {
        }

        recentlyEditedArr.unshift(templateId);

        const recentlyEditedSet = new Set<number>();
        recentlyEditedArr.forEach((templateId) => {
            recentlyEditedSet.add(templateId);
        });
        localStorage.setItem(localRecentlyEditedTemplates, JSON.stringify(Array.from(recentlyEditedSet)));

        return Promise.resolve(Array.from(recentlyEditedSet));
    };

    getRecentlyEditedTemplates = (): Promise<number[]> => {
        let recentlySeenArr: number[] = [];
        try {
            const recentlySeen = localStorage.getItem(localRecentlyEditedTemplates);
            if (recentlySeen) {
                recentlySeenArr = JSON.parse(recentlySeen);
            }
        } catch (e) {
        }

        return Promise.resolve(recentlySeenArr);
    };
}
